import { useState, useRef, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Save, RotateCcw, Settings, Eye } from 'lucide-react'
import { ConfigEditor, ConfigEditorHandle } from '@/components/config/ConfigEditor'
import { TeacherPreviewModal } from '@/components/config/TeacherPreviewModal'
import { HRPreviewModal } from '@/components/config/HRPreviewModal'
import { HolidayPeriodList } from '@/components/holidays/HolidayPeriodList'
import { useAuthStore } from '@/store'

export function ConfigPage({ forcedType, listOnly }: { forcedType?: string; listOnly?: boolean } = {}) {
  const { configType } = useParams<{ configType: string }>()
  const role = useAuthStore((s) => s.user?.role)
  const editorRef = useRef<ConfigEditorHandle>(null)
  const successTimeoutRef = useRef<number | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [configLabel, setConfigLabel] = useState('')
  const [configDescription, setConfigDescription] = useState('')
  const [showTeacherPreview, setShowTeacherPreview] = useState(false)
  const [showHrPreview, setShowHrPreview] = useState(false)

  const ct = forcedType || configType || 'substitution_request'
  const hideEditor = Boolean(listOnly || (ct === 'calendar' && role === 'admin'))

  useEffect(() => {
    setConfigLabel(hideEditor && ct === 'calendar' ? 'Kalender' : '')
    setConfigDescription('')
    setError(null)
    setSuccess(false)
    setDirty(false)
  }, [ct, hideEditor])

  const handleConfigLoaded = useCallback((label: string, description: string) => {
    setConfigLabel(label)
    setConfigDescription(description)
  }, [])

  const handleError = useCallback((error: string | null) => {
    setError(error)
  }, [])

  const handleSuccess = useCallback(() => {
    setSuccess(true)
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current)
    }
    successTimeoutRef.current = setTimeout(() => setSuccess(false), 5000)
  }, [])

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    setDirty(isDirty)
  }, [])

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  const handleSave = async () => {
    if (!editorRef.current?.isDirty()) return
    setSaving(true)
    await editorRef.current.save()
    setSaving(false)
  }

  const handleReset = () => {
    editorRef.current?.reset()
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <div className="px-4 lg:px-6 py-3 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <h1 className="text-lg font-bold">{configLabel || ct.replace(/_/g, ' ')}</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* hr2-v2 (E8): HR-Config zeigt die echte HR-Ansicht als Vorschau
              (gespeicherter Zustand) statt der Lehrer-Vorschau. */}
          {hideEditor ? null : ct === 'hr' ? (
            <button
              onClick={() => setShowHrPreview(true)}
              className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center gap-1.5"
              title="So sieht HR die freigegebenen Einstellungen (gespeicherter Zustand)"
            >
              <Eye className="w-4 h-4" /> HR-Vorschau
            </button>
          ) : ct !== 'email' && ct !== 'integration' && ct !== 'design' && ct !== 'dm' && ct !== 'talk' && (
            <button
              onClick={() => setShowTeacherPreview(true)}
              className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center gap-1.5"
              title="So sehen Lehrpersonen die freigegebenen Einstellungen (gespeicherter Zustand)"
            >
              <Eye className="w-4 h-4" /> Lehrer-Vorschau
            </button>
          )}
          {!hideEditor && dirty && (
            <button onClick={handleReset} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1">
              <RotateCcw className="w-3.5 h-3.5" /> Verwerfen
            </button>
          )}
          {!hideEditor && <button onClick={handleSave} disabled={saving || !dirty}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Speichern...' : 'Speichern'}
          </button>}
        </div>
      </div>

      {configDescription && (
        <div className="px-4 lg:px-6 py-3 bg-white border-b">
          <p className="text-sm text-gray-600">{configDescription}</p>
        </div>
      )}

      {showTeacherPreview && ct !== 'hr' && (
        <TeacherPreviewModal configType={ct} onClose={() => setShowTeacherPreview(false)} />
      )}

      {showHrPreview && ct === 'hr' && (
        <HRPreviewModal onClose={() => setShowHrPreview(false)} />
      )}

      {error && <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="bg-green-50 px-4 lg:px-6 py-3 text-sm text-green-700">Konfiguration gespeichert</div>}

      {ct === 'calendar' && <HolidayPeriodList />}

      {!hideEditor && (
      <ConfigEditor
        ref={editorRef}
        configType={ct}
        hideDelegation={ct === 'dm' || ct === 'talk'}
        onDirtyChange={handleDirtyChange}
        onError={handleError}
        onSuccess={handleSuccess}
        onConfigLoaded={handleConfigLoaded}
      />
      )}
    </div>
  )
}
